import { type FC, useState, useEffect, useMemo, useContext, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import Prompt from './Prompt';
import ModelSelector from './ModelSelector';
import VideoProperties from './VideoProperties';
import CoreUpsell from './CoreUpsell';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import {
  getVideoCreatorErrorCode,
  getVideoCreatorPricePoints,
  getVideoCreatorPrompt,
  getVideoCreatorStatus,
  getVideoCreatorIsCalculatingCost,
  getVideoCreatorGenerationCostModel,
  getVideoCreatorRestrictions,
  getVideoCreatorModel,
  getVideoCreatorModelsExpanded,
  getVideoCreatorDuration,
  getVideoCreatorSound,
  getVideoCreatorLowestPricePoints,
} from '../../../selectors/studio/videoCreator';
import { VideoCreatorErrorCode } from '../../../types/store';
import { getModelPropertiesByType } from '../ModelProperties';
import {
  generateVideo,
  updateVideoCreatorErrorCode,
  fetchGenerationCost,
} from '../../../actions/studio/videoCreator';
import VideoCreatorPointsModal from '../../../modals/VideoCreatorPointsModal';
import VideoLegalModal from '../../../modals/VideoLegalModal';
import { Url } from '@wix/da-url';
import { useFirstTimeFlag } from '@wix/da-shared-react/pkg/redux/firstTimeFlags/hooks';
import { FirstTimeFlag } from '@wix/da-shared-react/pkg/redux/firstTimeFlags';
import { getFirstTimeFlags } from '@wix/da-shared-react/pkg/redux/firstTimeFlags/selectors';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import {
  BiData,
  BuyCoreButtonBiEvent,
  VideoCreatorCreateButtonClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { useWindowResizeEvent } from '@wix/da-hooks/pkg/useWindowResizeEvent';
import { VIDEO_CREATOR_LAYOUT } from './VideoCreatorControls.constants';

import s from './VideoCreatorControls.scss';

const LONG_PROMPT_LENGTH = 50;

export interface Props {
  className?: string;
}

const VideoCreatorControls: FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const logBiEvent = useBiLogger();

  const isMobile = useContext(MobileContext);
  const { hasCoreMembership } = useContext(SessionContext);
  const restrictions = useSelector(getVideoCreatorRestrictions);
  const dispatch = useDispatch();
  const pricePoints = useSelector(getVideoCreatorPricePoints);
  const lowestPricePoints = useSelector(getVideoCreatorLowestPricePoints);
  const prompt = useSelector(getVideoCreatorPrompt);
  const errorCode = useSelector(getVideoCreatorErrorCode);
  const isBusy = useSelector(getVideoCreatorStatus) === 'busy';
  const selectedModel = useSelector(getVideoCreatorModel);
  const duration = useSelector(getVideoCreatorDuration);
  const sound = useSelector(getVideoCreatorSound);
  const isCalculatingCost = useSelector(getVideoCreatorIsCalculatingCost);
  const generationCostModel = useSelector(getVideoCreatorGenerationCostModel);
  const firstTimeFlags = useSelector(getFirstTimeFlags);
  const isModelsExpanded = useSelector(getVideoCreatorModelsExpanded);

  const [showModelsExpansionControls, setShowModelsExpansionControls] = useState(() =>
    typeof window !== 'undefined'
      ? window.innerHeight < VIDEO_CREATOR_LAYOUT.windowSizeThreshold
      : false
  );

  const onWindowResize = useCallback((_width: number, height: number) => {
    setShowModelsExpansionControls(height < VIDEO_CREATOR_LAYOUT.windowSizeThreshold);
  }, []);

  useWindowResizeEvent(onWindowResize, true);

  // Client-side fallback: SSR may serialize the store before generation_cost
  // completes (often with isCalculatingCost still true). Trigger fetch when we have no price.
  useEffect(() => {
    if (pricePoints) return;
    const model = getModelPropertiesByType(selectedModel);
    if (model?.id) {
      dispatch(fetchGenerationCost(model.id, duration, sound));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- only on mount

  const isOutOfPrompts = useMemo(() => {
    if (!hasCoreMembership) {
      return restrictions?.videoPromptsRemaining < 1;
    } else {
      return false;
    }
  }, [hasCoreMembership, restrictions?.videoPromptsRemaining]);

  const { dropFlag } = useFirstTimeFlag(FirstTimeFlag.IsVideoCreatorModalNotSeen);
  const hasError =
    errorCode !== VideoCreatorErrorCode.NO_ERROR &&
    errorCode !== VideoCreatorErrorCode.INSUFFICIENT_POINTS_BALANCE;
  const [isPointsModalOpen, setIsPointsModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const hasLongPrompt = prompt.length > LONG_PROMPT_LENGTH;

  const availableModels = useMemo(() => restrictions?.videoModelsAvailable ?? [], [restrictions]);
  const model = useMemo(() => {
    return getModelPropertiesByType(selectedModel);
  }, [selectedModel]);
  const showAudio = model ? model.hasSound !== 'no' : false;

  const isModelAvailable = useMemo(() => {
    const index = availableModels.findIndex(id => id === model?.id);
    return index >= 0;
  }, [availableModels, model?.id]);

  const redirectToCore = !isModelAvailable || isOutOfPrompts;
  const buttonVariant = redirectToCore ? 'core-orange' : 'brand-green';
  const cachedTextRef = useRef('');
  const buttonText = useMemo(() => {
    if (!pricePoints) {
      return '';
    }

    // We don't want to flip the text to "calculating" because the calculation happens so
    // fast that it looks broken. But, we also don't want it to briefly flash the wrong number (which
    // it will do if they are switching models), so instead just force it to leave the old text
    // while the calculation is happening.
    if (isCalculatingCost) {
      return cachedTextRef.current;
    }
    // Price is for a different model (user switched); keep showing previous text until new cost loads.
    if (generationCostModel != null && model?.id != null && model.id !== generationCostModel) {
      return cachedTextRef.current;
    }
    let text: string;
    if (redirectToCore && lowestPricePoints) {
      text = t('studio.videoCreator.createButton.upgradeVariant', {
        postProcess: 'reactPostprocessor',
        points: lowestPricePoints,
      });
    } else {
      text = t('studio.videoCreator.createButton', {
        postProcess: 'reactPostprocessor',
        points: pricePoints,
      });
    }
    cachedTextRef.current = text;
    return text;
  }, [
    redirectToCore,
    lowestPricePoints,
    pricePoints,
    t,
    isCalculatingCost,
    generationCostModel,
    model?.id,
  ]);

  const canGenerate = useMemo(() => {
    if (isBusy || !pricePoints || prompt.length === 0) {
      return false;
    }
    return true;
  }, [pricePoints, prompt, isBusy]);

  const pointsBalance = restrictions?.pointsBalance ?? 0;
  const hasEnoughPoints = pricePoints > 0 && pointsBalance >= pricePoints;

  // Undefined or false means they've seen the modal, so don't show it
  const showFirstGenModal = firstTimeFlags[FirstTimeFlag.IsVideoCreatorModalNotSeen] === true;

  useEffect(() => {
    if (errorCode === VideoCreatorErrorCode.INSUFFICIENT_POINTS_BALANCE) {
      setIsPointsModalOpen(true);
      dispatch(updateVideoCreatorErrorCode(VideoCreatorErrorCode.NO_ERROR));
    }
  }, [errorCode, dispatch]);

  const templateRows = useMemo(() => {
    const {
      promptShortNoError,
      promptErrorDelta,
      promptLongDelta,
      promptMobileDelta,
      propertiesHeightNoAudio,
      propertiesAudioDelta,
      modelAreaHeight,
      modelAreaHeightExpanded,
      modelAreaHeightLargeScreen,
      modelAreaHeightMobile,
    } = VIDEO_CREATOR_LAYOUT;

    let promptHeight = promptShortNoError;
    if (hasError) {
      promptHeight += promptErrorDelta;
    }
    if (hasLongPrompt) {
      promptHeight += promptLongDelta;
    }
    if (isMobile) {
      promptHeight -= promptMobileDelta;
    }

    let propertiesHeight = propertiesHeightNoAudio;
    if (showAudio) {
      propertiesHeight += propertiesAudioDelta;
    }
    const modelHeight = isMobile
      ? modelAreaHeightMobile
      : !showModelsExpansionControls
        ? modelAreaHeightLargeScreen
        : isModelsExpanded
          ? modelAreaHeightExpanded
          : modelAreaHeight;

    return `${promptHeight}px ${modelHeight}px ${propertiesHeight}px 1fr`;
  }, [hasError, hasLongPrompt, showAudio, isMobile, isModelsExpanded, showModelsExpansionControls]);

  const handleGenerateClick = () => {
    // We don't want to allow clicks while the displayed price could be wrong, but disabling
    // the button makes the button flash and looks like a glitch. Everything usually happens so fast,
    // but this is a failsafe in case the generation_cost endpoint takes longer than normal.
    if (isCalculatingCost) {
      return;
    }
    if (!isModelAvailable || isOutOfPrompts) {
      logBiEvent(
        BiData<BuyCoreButtonBiEvent>({
          evid: 375,
          referral_info: ReferralType.VIDEO_CREATOR_CREATE_BUTTON,
        })
      );
      window.open(Url.coreMembershipLink({ referer: Url.videoCreatorLink() }), '_blank');
      return;
    }
    if (!hasEnoughPoints) {
      logBiEvent(
        BiData<VideoCreatorCreateButtonClickBiEvent>({
          evid: 950,
          prompt: prompt,
          ai_model: model?.id ?? '',
          is_valid: false,
          error: 'insufficient_points',
        })
      );
      setIsPointsModalOpen(true);
      return;
    }
    if (!showFirstGenModal) {
      dispatch(generateVideo());
    } else {
      setIsConfirmationModalOpen(true);
    }
  };

  const handleConfirmationConfirm = () => {
    dropFlag();
    setIsConfirmationModalOpen(false);
    dispatch(generateVideo());
  };

  const handleConfirmationClose = () => {
    dropFlag();
    setIsConfirmationModalOpen(false);
  };

  return (
    <div
      className={cn(
        s['root'],
        hasError && s['has-error'],
        isMobile && s['mobile'],
        !isMobile && s['desktop'],
        className
      )}
    >
      <div
        className={cn(
          s['scroller'],
          showAudio && s['has-audio'],
          hasLongPrompt && s['long-prompt']
        )}
        style={{ gridTemplateRows: templateRows }}
      >
        <Prompt />
        <ModelSelector showExpansionControls={showModelsExpansionControls} />
        <VideoProperties />
        {!isMobile && <CoreUpsell />}
      </div>
      <div className={s['create-area']}>
        <BrandedButton
          className={cn(s['create-button'], isCalculatingCost && s['is-calculating'])}
          key="submit"
          variant={buttonVariant}
          size="large"
          disabled={!canGenerate}
          onClick={handleGenerateClick}
        >
          {!pricePoints && (
            <div className={s['button-calculating']}>
              <div className={s['spinner-anchor']}>
                <div className={s['button-spinner']} />
              </div>
              <span className={s['spinner-label']}>
                {t('studio.videoCreator.creatButton.calculating')}
              </span>
            </div>
          )}
          {pricePoints && <>{buttonText}</>}
        </BrandedButton>
      </div>
      {isMobile && <CoreUpsell />}
      <VideoLegalModal
        isOpen={isConfirmationModalOpen}
        onConfirm={handleConfirmationConfirm}
        onRequestClose={handleConfirmationClose}
      />
      <VideoCreatorPointsModal
        isOpen={isPointsModalOpen}
        onRequestClose={() => setIsPointsModalOpen(false)}
      />
    </div>
  );
};

VideoCreatorControls.displayName = 'VideoCreatorControls';
export default VideoCreatorControls;
